import React from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { MenuItem, Grid, Button } from "@mui/material";
import { NON_CONTRACT, SHIFT_BID_PDF_EXPORT_TYPES, ADMIN_PDF_HEADER, EMPLOYEE_PDF_HEADER } from "../../util/constants";
import {
  POSITIONS_AND_ASSIGNMENTS_ARE_SUBJECT_TO_CHANGE_MESSAGE,
  BLOCK_LINE_ASTERISK_MESSAGE,
} from "../../util/messageConstants";
import { getShiftDate } from "../../util/dateTimeFormatter";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import _ from "lodash";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import { BID_STATUS } from "../../util/bid/bidConstants";
import { fetchEmployee } from "../../api/employee";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PropTypes from "prop-types";

const generateFilenameForAdminAwardPDFExport = ({
  station,
  workgroup,
  bidType,
  bidName,
  schedStartDate,
  schedEndDate,
}) => {
  return `STARS_${station}_${workgroup}_${bidType}_${bidName}_Results_${getShiftDate(schedStartDate)}_to_${getShiftDate(
    schedEndDate
  )}.pdf`;
};

const generateFilenameForEmployeeAwardPDFExport = ({ station, workgroup, bidType, schedStartDate, schedEndDate }) => {
  return `STARS_${station}_${workgroup}_${bidType}_Results_Emp_View_${getShiftDate(schedStartDate)}_to_${getShiftDate(
    schedEndDate
  )}.pdf`;
};

const generateFilenameForAdminBidLinePDFExport = (bidData) => {
  return `STARS_${bidData.station}_${bidData.workgroup}_Shift_Bid_Lines_${getShiftDate(
    bidData.schedStartDate
  )}_to_${getShiftDate(bidData.schedEndDate)}.pdf`;
};

const generateFilenameForBidLinePDFExport = (eid, schedStartDate, schedEndDate) => {
  return `Shift_Bid_Lines_${eid}_${getShiftDate(schedStartDate)}_to_${getShiftDate(schedEndDate)}.pdf`;
};

const generateFilenameForChoicesPDFExport = (date) => {
  return `Shift_Bid_Choices_${date}.pdf`;
};

const fetchEmployeeData = async (eid) => {
  return await fetchEmployee(eid)
    .then((res) => res.json())
    .then((result) => {
      if (!result.error && !result.message) {
        return result;
      } else {
        console.log("Invalid Employee ID");
      }
    })
    .catch((err) => {
      console.error("error fetching employee data!", err);
      return null;
    });
};

const ShiftBidPDFExport = (props) => {
  const contract = getUnionContractGivenWorkgroup(props.bidChoiceData?.workgroup);

  const downloadBidLinesPdf = (pdfData, columnList, fileName, isAdmin) => {
    const doc = new jsPDF("p", "mm");
    const totalPagesExp = "{total_pages_count_string}";
    const bidWindowTime = props.bidChoiceData.windowStartDate.toFormat("T ZZZZ");

    doc.autoTable({
      theme: "grid",
      bodyStyles: { lineColor: "#e0e0e0", fontSize: 8 },
      styles: { fontSize: 10, cellPadding: { top: 1, right: 2, bottom: 0, left: 2, lineWidth: 0 } },
      headStyles: {
        fillColor: "#e0e0e0",
        textColor: "#000000",
        lineColor: "#b5b2b1",
        lineWidth: 0.1,
        fontSize: 8,
      },
      columnStyles: {
        assignedEmployee: {
          cellWidth: 19,
        },
      },

      columns: columnList,
      body: pdfData.map((row) => Object.values(row)),
      margin: { top: 35 },
      didParseCell: function (data) {
        if (data.section === "body") {
          switch (data.column.raw.title) {
            case "Block Line":
              if (!_.isEmpty(data.cell.raw)) {
                data.cell.text =
                  data.cell.raw.lastName !== "Unassigned"
                    ? `${data.cell.raw.lastName}, ${data.cell.raw.firstName}`
                    : "Unassigned";
              } else {
                data.cell.text = "";
              }
              break;
            case "Sun":
            case "Mon":
            case "Tue":
            case "Wed":
            case "Thu":
            case "Fri":
            case "Sat":
              data.cell.styles.fillColor = "#f5f5f5";
              if (data.cell.raw === "OFF") {
                data.cell.styles.fillColor = "#e0e0e0";
              }
              break;
            default:
              data.cell.styles.fillColor = "white";
              break;
          }
        }
      },
      didDrawPage: function (data) {
        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(40);
        doc.text(
          `Shift Bid: ${props.bidChoiceData.station} | ${props.bidChoiceData.workgroup} | ${getShiftDate(
            props.bidChoiceData.schedStartDate
          )} - ${getShiftDate(props.bidChoiceData.schedEndDate)}`,
          14,
          14
        );

        doc.setFillColor("#e0e0e0");
        doc.rect(14, 20, 182, 11, "F");

        doc.setFontSize(10);
        doc.setTextColor("#5a5a5a");
        doc.text("BID WINDOW: ", 17, 27);
        doc.setTextColor("#1d1d1d");
        doc.text(
          `${getShiftDate(props.bidChoiceData.windowStartDate)} - ${getShiftDate(
            props.bidChoiceData.windowEndDate
          )}, ${bidWindowTime} `,
          doc.internal.pageSize.getWidth() - 168,
          27
        );
        doc.setTextColor("#5a5a5a");
        if (isAdmin) {
          const ftLines = props.bidLines.filter((line) => line.ftpt === "FT").length;
          const ptLines = props.bidLines.filter((line) => line.ftpt === "PT").length;

          doc.text(`BID LINES: ${ftLines} FT ${ptLines} PT`, 192, 27, null, null, "right");
        } else {
          doc.text(
            `${props.bidLines.length} ${props.bidChoiceData.ftpt} BID ${props.bidLines.length > 1 ? "LINES" : "LINE"}`,
            192,
            27,
            null,
            null,
            "right"
          );
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        //footer
        var currentpage = 0;
        if (currentpage < data.pageCount) {
          doc.text(
            data.pageCount + " of " + totalPagesExp,
            doc.internal.pageSize.getWidth() - 21,
            doc.internal.pageSize.height - 10
          );

          doc.text(POSITIONS_AND_ASSIGNMENTS_ARE_SUBJECT_TO_CHANGE_MESSAGE, 15, doc.internal.pageSize.height - 10);
          currentpage = data.pageCount;
        }
      },
    });
    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }

    doc.save(fileName);
  };

  const downloadBidChoicesPdf = async (pdfData, columnList, fileName) => {
    const doc = new jsPDF("p", "mm");
    const totalPagesExp = "{total_pages_count_string}";
    const employeeData = await fetchEmployeeData(props.bidChoiceData.eid);
    doc.autoTable({
      theme: "grid",
      bodyStyles: { lineColor: "#e0e0e0", fontSize: 8 },
      styles: { fontSize: 10, cellPadding: { top: 1, right: 2, bottom: 0, left: 2, lineWidth: 0 } },
      headStyles: {
        fillColor: "#e0e0e0",
        textColor: "#000000",
        lineColor: "#b5b2b1",
        lineWidth: 0.1,
        fontSize: 8,
      },
      columnStyles: {
        assignedEmployee: {
          cellWidth: 19,
        },
      },

      columns: columnList,
      body: pdfData.map((row) => Object.values(row)),
      margin: { top: 42 },
      didParseCell: function (data) {
        if (data.section === "body") {
          switch (data.column.raw.title) {
            case "Block Line":
              if (!_.isEmpty(data.cell.raw)) {
                data.cell.text =
                  data.cell.raw.lastName !== "Unassigned"
                    ? `${data.cell.raw.lastName}, ${data.cell.raw.firstName}`
                    : "Unassigned";
              } else {
                data.cell.text = "";
              }
              break;
            case "Sun":
            case "Mon":
            case "Tue":
            case "Wed":
            case "Thu":
            case "Fri":
            case "Sat":
              data.cell.styles.fillColor = "#f5f5f5";
              if (data.cell.raw === "OFF") {
                data.cell.styles.fillColor = "#e0e0e0";
              }
              break;
            default:
              data.cell.styles.fillColor = "white";
              break;
          }
        }
      },
      didDrawPage: function (data) {
        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(40);
        doc.text(
          `Shift Bid: ${props.bidChoiceData.station} | ${props.bidChoiceData.workgroup} | ${getShiftDate(
            props.bidChoiceData.schedStartDate
          )} - ${getShiftDate(props.bidChoiceData.schedEndDate)}`,
          14,
          14
        );

        doc.setFillColor("#e0e0e0");
        doc.rect(14, 20, 182, 18, "F");
        doc.setFontSize(10);
        doc.setTextColor("#1d1d1d");
        doc.text(`${employeeData?.lastName}, ${employeeData?.shortName}  (${props.bidChoiceData?.eid})`, 17, 27);
        doc.setTextColor("#5a5a5a");
        doc.text("CHOICES ENTERED: ", 17, 35);
        doc.setTextColor("#1d1d1d");
        doc.text(`${pdfData.length}`, doc.internal.pageSize.getWidth() - 157, 35);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        //footer
        let currentpage = 0;
        if (currentpage < data.pageCount) {
          doc.text(
            data.pageCount + " of " + totalPagesExp,
            doc.internal.pageSize.getWidth() - 21,
            doc.internal.pageSize.height - 10
          );

          doc.text(POSITIONS_AND_ASSIGNMENTS_ARE_SUBJECT_TO_CHANGE_MESSAGE, 15, doc.internal.pageSize.height - 10);
          currentpage = data.pageCount;
        }
      },
    });
    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }

    doc.save(fileName);
  };

  const formatDataForPDF = (bidLines, pdfType, isAdmin = false) => {
    let formattedBidLines = [];

    let choiceIndex = 0;
    for (const bidLine of bidLines) {
      let formattedBidLine = {};

      if (pdfType === SHIFT_BID_PDF_EXPORT_TYPES.CHOICES) formattedBidLine["choices"] = choiceIndex + 1;
      formattedBidLine["lineNumber"] = bidLine.lineNumber;

      if (pdfType === SHIFT_BID_PDF_EXPORT_TYPES.BID_LINES && isAdmin) formattedBidLine["ftpt"] = bidLine.ftpt;

      if (contract === NON_CONTRACT) {
        // For non-contract bids:
        // if the bidLine is an assigned block line, then show the assigned employee name,
        // if it is unassigned block line then show "Unassigned"
        // else leave it blank

        if (bidLine.isBlockLine) {
          if (!bidLine.assignedEmployee) {
            formattedBidLine["assignedEmployee"] = {
              lastName: "Unassigned",
            };
          } else {
            formattedBidLine["assignedEmployee"] = bidLine.assignedEmployee;
          }
        } else {
          formattedBidLine["assignedEmployee"] = {};
        }
      }

      formattedBidLine["shiftDiff"] = bidLine.shiftDiff;
      for (const shift of bidLine.shifts) {
        // eslint-disable-next-line
        switch (shift.day) {
          case "Sunday":
            formattedBidLine["Sunday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Monday":
            formattedBidLine["Monday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Tuesday":
            formattedBidLine["Tuesday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Wednesday":
            formattedBidLine["Wednesday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Thursday":
            formattedBidLine["Thursday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Friday":
            formattedBidLine["Friday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Saturday":
            formattedBidLine["Saturday"] = shift.isOff
              ? "OFF"
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
        }
      }
      formattedBidLines.push(formattedBidLine);
      choiceIndex++;
    }
    return formattedBidLines;
  };

  const getColumnListForPDFExport = (pdfType, isAdmin) => {
    const nonContractColumns = [
      { title: "Choice", dataKey: "choices" },
      { title: "Bid Line", dataKey: "lineNumber" },
      { title: "FT/\nPT", dataKey: "ftpt" },
      { title: "Block Line", dataKey: "assignedEmployee" },
      { title: "SD", dataKey: "shiftDiff" },
      { title: "Sun", dataKey: "Sunday" },
      { title: "Mon", dataKey: "Monday" },
      { title: "Tue", dataKey: "Tuesday" },
      { title: "Wed", dataKey: "Wednesday" },
      { title: "Thu", dataKey: "Thursday" },
      { title: "Fri", dataKey: "Friday" },
      { title: "Sat", dataKey: "Saturday" },
    ];

    let columnList;

    if (contract === NON_CONTRACT) {
      columnList = nonContractColumns;
    } else {
      // Exclude blockline column for IAM/ TWU
      const exclusionList = ["assignedEmployee"];
      columnList = nonContractColumns.filter((item) => !exclusionList.includes(item.dataKey));
    }

    if (pdfType === SHIFT_BID_PDF_EXPORT_TYPES.BID_LINES) {
      // Bid lines PDF should not have choices column, so exclude it
      const exclusionList = ["choices"];
      columnList = columnList.filter((item) => !exclusionList.includes(item.dataKey));
    }
    if (!isAdmin) {
      const exclusionList = ["ftpt"];
      columnList = columnList.filter((item) => !exclusionList.includes(item.dataKey));
    }

    return columnList;
  };

  const handleExportClick = (pdfType, isAdmin = false) => {
    try {
      let pdfData, fileName;
      const columnList = getColumnListForPDFExport(pdfType, isAdmin);

      if (pdfType === SHIFT_BID_PDF_EXPORT_TYPES.BID_LINES) {
        pdfData = formatDataForPDF(props.bidLines, pdfType, isAdmin);
        fileName = isAdmin
          ? generateFilenameForAdminBidLinePDFExport(props.bidChoiceData)
          : generateFilenameForBidLinePDFExport(
              props.eid,
              props.bidChoiceData.schedStartDate,
              props.bidChoiceData.schedEndDate
            );
        downloadBidLinesPdf(pdfData, columnList, fileName, isAdmin);
      }

      if (pdfType === SHIFT_BID_PDF_EXPORT_TYPES.CHOICES) {
        pdfData = formatDataForPDF(props.selectedBidLines, pdfType);
        const lastSubmittedDate = getStationTimeZoneDateTimeStringFromUTCInteger(
          props.lastSubmittedTimestamp,
          props.bidChoiceData.station
        ).toFormat("yyyy-MM-dd_HHmm");
        fileName = generateFilenameForChoicesPDFExport(lastSubmittedDate);
        downloadBidChoicesPdf(pdfData, columnList, fileName);
      }

      if (
        props.isDesktopView ||
        props.bidChoiceData.status === BID_STATUS.CLOSED ||
        props.bidChoiceData.status === BID_STATUS.AWARDED
      ) {
        props.onExportMenuClose?.();
      }
    } catch (e) {
      console.log("Error during downloading pdf!", e);
    }
  };

  const downloadAwardPdf = (pdfType, pdfData, columnList, fileName) => {
    const doc = new jsPDF("l", "mm");
    const totalPagesExp = "{total_pages_count_string}";
    const isAdmin = pdfType === ADMIN_PDF_HEADER;
    const exclusionList = ["award", "awardedRank"];
    const employeeColumns = columnList.filter((item) => !exclusionList.includes(item.dataKey));
    const height = contract === NON_CONTRACT ? 15 : 10;
    doc.autoTable({
      theme: "grid",
      bodyStyles: { lineColor: "#e0e0e0", fontSize: 7 },
      styles: { fontSize: 10, cellPadding: { top: 1, right: 1, bottom: 0, left: 1, lineWidth: 0 } },
      headStyles: {
        fillColor: "#e0e0e0",
        textColor: "#000000",
        lineColor: "#b5b2b1",
        lineWidth: 0.1,
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 10 },
        2: { cellWidth: 25 },
      },
      columns: isAdmin
        ? columnList && columnList.map((col) => ({ ...col, dataKey: col.field }))
        : employeeColumns && employeeColumns.map((col) => ({ ...col, dataKey: col.field })),
      body: pdfData.map((row) => Object.values(row)),
      margin: contract === NON_CONTRACT ? { top: 20, bottom: 20 } : { top: 20 },
      rowPageBreak: "avoid",
      didParseCell: function (data) {
        if (data.section === "body") {
          switch (data.column.raw.title) {
            case "Sun":
            case "Mon":
            case "Tue":
            case "Wed":
            case "Thu":
            case "Fri":
            case "Sat":
              data.cell.styles.fillColor = "#f5f5f5";
              if (data.cell.raw === "OFF") {
                data.cell.styles.fillColor = "#e0e0e0";
              }
              break;
            default:
              data.cell.styles.fillColor = "white";
              break;
          }
        }
      },
      didDrawPage: function (data) {
        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(40);
        doc.text(
          `Shift Bid: ${props.bidChoiceData.station} | ${props.bidChoiceData.workgroup} | ${getShiftDate(
            props.bidChoiceData.schedStartDate
          )} - ${getShiftDate(props.bidChoiceData.schedEndDate)}`,
          14,
          14
        );

        doc.setFontSize(10);
        doc.setTextColor("#5a5a5a");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        //footer
        let currentpage = 0;
        if (currentpage < data.pageCount) {
          doc.text(
            data.pageCount + " of " + totalPagesExp,
            doc.internal.pageSize.getWidth() - 21,
            doc.internal.pageSize.height - height
          );

          doc.text(POSITIONS_AND_ASSIGNMENTS_ARE_SUBJECT_TO_CHANGE_MESSAGE, 15, doc.internal.pageSize.height - height);
          if (contract === NON_CONTRACT) doc.text(BLOCK_LINE_ASTERISK_MESSAGE, 15, doc.internal.pageSize.height - 10);
          currentpage = data.pageCount;
        }
      },
    });
    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }

    doc.save(fileName);
  };

  const formatDataForAwardPDF = (bidLines, pdfType) => {
    let formattedBidLines = [];

    for (const bidLine of bidLines) {
      let formattedBidLine = {};
      formattedBidLine["lineNumber"] =
        contract === NON_CONTRACT && bidLine.isBlockLine ? `${bidLine.lineNumber}*` : bidLine.lineNumber;
      formattedBidLine["eid"] = bidLine.awardedEid;
      formattedBidLine["name"] =
        bidLine.awardedLastName && bidLine.awardedShortName
          ? `${bidLine.awardedLastName},\n${bidLine.awardedShortName}`
          : "";
      if (pdfType === ADMIN_PDF_HEADER) {
        formattedBidLine["awardedRank"] = bidLine.awardedRank;
      }
      formattedBidLine["ftpt"] = bidLine.ftpt;
      if (pdfType === ADMIN_PDF_HEADER) {
        formattedBidLine["award"] = bidLine.awardType;
      }
      formattedBidLine["shiftDiff"] = bidLine.shiftDiff;
      for (const shift of bidLine.shifts) {
        switch (shift.day) {
          case "Sunday":
            formattedBidLine["Sunday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Monday":
            formattedBidLine["Monday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Tuesday":
            formattedBidLine["Tuesday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Wednesday":
            formattedBidLine["Wednesday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Thursday":
            formattedBidLine["Thursday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Friday":
            formattedBidLine["Friday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          case "Saturday":
            formattedBidLine["Saturday"] = shift.isOff
              ? "OFF"
              : pdfType === ADMIN_PDF_HEADER
              ? `${shift.start}-${shift.end}\n${shift.position}\n${shift.assignment}`
              : props.useAssignmentForEmp
              ? `${shift.start}-${shift.end}\n${shift.assignment}`
              : `${shift.start}-${shift.end}\n${shift.position}`;
            break;
          default:
            console.error("Unrecognized shift day: ", shift.day);
        }
      }
      formattedBidLines.push(formattedBidLine);
    }
    return formattedBidLines;
  };

  const handleAwardExportClick = (pdfType) => {
    try {
      let pdfData, fileName;
      const columnList = [
        { title: "Bid Line", dataKey: "lineNumber" },
        { title: "EID", dataKey: "eid" },
        { title: "Name", dataKey: "name" },
        { title: "Bid Group\nRank", dataKey: "awardedRank" },
        { title: "FT/\nPT", dataKey: "ftpt" },
        { title: "Award", dataKey: "award" },
        { title: "SD", dataKey: "shiftDiff" },
        { title: "Sun", dataKey: "Sunday" },
        { title: "Mon", dataKey: "Monday" },
        { title: "Tue", dataKey: "Tuesday" },
        { title: "Wed", dataKey: "Wednesday" },
        { title: "Thu", dataKey: "Thursday" },
        { title: "Fri", dataKey: "Friday" },
        { title: "Sat", dataKey: "Saturday" },
      ];
      if (pdfType === ADMIN_PDF_HEADER) {
        pdfData = formatDataForAwardPDF(props.bidLines, pdfType);
        fileName = generateFilenameForAdminAwardPDFExport(props.bidChoiceData);
      } else if (pdfType === EMPLOYEE_PDF_HEADER) {
        pdfData = formatDataForAwardPDF(props.bidLines, pdfType);
        fileName = generateFilenameForEmployeeAwardPDFExport(props.bidChoiceData);
      }
      downloadAwardPdf(pdfType, pdfData, columnList, fileName);
      props.onExportMenuClose?.();
    } catch (e) {
      console.log("Error during downloading pdf!", e);
    }
  };

  const renderAdminAwardExportButtons = () => {
    return (
      <>
        <MenuItem
          disabled={props.bidChoiceData.status !== BID_STATUS.AWARDED}
          onClick={() => {
            handleAwardExportClick(ADMIN_PDF_HEADER);
          }}
        >
          Admin PDF
        </MenuItem>
        <MenuItem
          disabled={props.bidChoiceData.status !== BID_STATUS.AWARDED}
          onClick={() => {
            handleAwardExportClick(EMPLOYEE_PDF_HEADER);
          }}
        >
          Employee View PDF
        </MenuItem>
      </>
    );
  };

  const renderEmployeeAwardExportButtons = () => {
    return (
      <Grid container justifyContent={"center"} sx={{ marginTop: "20px" }}>
        <Grid>
          <Button
            component="button"
            variant="contained"
            color="primary"
            onClick={() => {
              handleAwardExportClick(EMPLOYEE_PDF_HEADER);
            }}
            disabled={props.isMyBidResultsDialogLoading}
          >
            EXPORT BID RESULTS (PDF)
          </Button>
        </Grid>
        <Grid style={{ marginTop: "10px" }}>
          <Button
            component="button"
            variant="contained"
            color="primary"
            onClick={() => {
              handleExportClick(SHIFT_BID_PDF_EXPORT_TYPES.CHOICES);
            }}
            disabled={props.isMyBidResultsDialogLoading}
          >
            EXPORT MY CHOICES (PDF)
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderClosedBidExportButtons = () => {
    return (
      <>
        <MenuItem
          disabled={!props.isBidLineExportAllowed}
          onClick={() => {
            handleExportClick(SHIFT_BID_PDF_EXPORT_TYPES.BID_LINES);
          }}
        >
          Bid Lines (PDF)
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExportClick(SHIFT_BID_PDF_EXPORT_TYPES.CHOICES);
          }}
        >
          My Choices (PDF)
        </MenuItem>
      </>
    );
  };

  const renderMobileExportButtons = () => {
    return (
      <Grid container justifyContent={"center"}>
        <Grid>
          <Button
            component="button"
            variant="contained"
            color="primary"
            disabled={!props.isBidLineExportAllowed}
            onClick={() => {
              handleExportClick(SHIFT_BID_PDF_EXPORT_TYPES.BID_LINES);
            }}
          >
            EXPORT BID LINES
          </Button>
        </Grid>
        <Grid style={{ marginTop: "10px" }}>
          <Button
            component="button"
            variant="contained"
            color="primary"
            onClick={() => {
              handleExportClick(SHIFT_BID_PDF_EXPORT_TYPES.CHOICES);
            }}
          >
            EXPORT MY CHOICES
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderAdminBidLineExportButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        disabled={
          props.bidChoiceData.status === BID_STATUS.PENDING || props.bidChoiceData.status === BID_STATUS.DELETED
        }
        sx={{
          margin: "5px",
        }}
        startIcon={<UploadFileIcon />}
        onClick={() => {
          handleExportClick(SHIFT_BID_PDF_EXPORT_TYPES.BID_LINES, true);
        }}
      >
        Export Bid Lines
      </Button>
    );
  };

  const renderExportButtons = () => {
    if (props.isAdminAwardExport) {
      return renderAdminAwardExportButtons();
    } else if (props.isEmployeeAwardExport) {
      return renderEmployeeAwardExportButtons();
    } else if (props.isAdminBidLineExport) {
      return renderAdminBidLineExportButton();
    } else if (
      props.isDesktopView ||
      props.bidChoiceData.status === BID_STATUS.CLOSED ||
      props.bidChoiceData.status === BID_STATUS.AWARDED
    ) {
      return renderClosedBidExportButtons();
    } else {
      return renderMobileExportButtons();
    }
  };

  return <>{renderExportButtons()}</>;
};

ShiftBidPDFExport.propTypes = {
  isDesktopView: PropTypes.bool,
  bidLines: PropTypes.array,
  selectedBidLines: PropTypes.array,
  bidChoiceData: PropTypes.object,
  isAdminAwardExport: PropTypes.bool,
  isAdminBidLineExport: PropTypes.bool,
  isEmployeeAwardExport: PropTypes.bool,
  isBidLineExportAllowed: PropTypes.bool,
  onExportMenuClose: PropTypes.func,
  eid: PropTypes.string,
  lastSubmittedTimestamp: PropTypes.number,
  isMyBidResultsDialogLoading: PropTypes.bool,
  useAssignmentForEmp: PropTypes.bool,
};

export default ShiftBidPDFExport;
